// frontend/src/components/BillingTab.js

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import {
  CheckCircleIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import { useBilling } from "./BillingContext";
import ReactDOM from "react-dom";
import { SearchIcon } from "lucide-react";
import { computeFixedDropdownPosition, fixedDropdownStyle } from "./dropdownPositionUtils";

// ─── Line item math ─────────────────────────────────────────────────────────

const computeLine = (item) => {
  const price = item.price || 0;
  const qty = item.quantity || 1;
  const discountPct = item.discountPct || 0;
  const taxPct = item.taxPct || 0;
  const priceIncludesTax = !!item.price_includes_tax;

  if (priceIncludesTax) {
    const discountedUnitPrice = taxPct > 0 ? price / (1 + taxPct / 100) : price;
    const taxAmtUnitPrice = price - discountedUnitPrice;
    const afterTaxUnitPrice = price;
    const base = price * qty;
    return { base, discountAmt: 0, taxAmt: taxAmtUnitPrice * qty, lineTotal: base, taxAmtUnitPrice, discountedUnitPrice, afterTaxUnitPrice };
  }

  const discountedUnitPrice = price * (1 - discountPct / 100);
  const taxAmtUnitPrice = (discountedUnitPrice * taxPct) / 100;
  const afterTaxUnitPrice = discountedUnitPrice * (1 + taxPct / 100);

  const base = price * qty;
  const discountAmt = (base * discountPct) / 100;
  const taxAmt = ((base - discountAmt) * taxPct) / 100;
  const lineTotal = base - discountAmt + taxAmt;

  return { base, discountAmt, taxAmt, lineTotal, taxAmtUnitPrice, discountedUnitPrice, afterTaxUnitPrice };
};

const sumCategory = (items) =>
  items.reduce(
    (acc, item) => {
      const { base, discountAmt, taxAmt, lineTotal } = computeLine(item);
      acc.base += base;
      acc.discountAmt += discountAmt;
      acc.taxAmt += taxAmt;
      acc.lineTotal += lineTotal;
      return acc;
    },
    { base: 0, discountAmt: 0, taxAmt: 0, lineTotal: 0 }
  );

const fmt = (n) => (Number(n) || 0).toFixed(0);
/** Price / tax split — keep decimals (e.g. ₹160.71 + ₹19.29). */
const fmtDec = (n) => (Number(n) || 0).toFixed(2);

// ─── Editable line-item row ────────────────────────────────────────────────

const ItemRow = ({ item, onChange, onRemove, readOnly = false, hideDeleteButton = false }) => {
  const { lineTotal, discountedUnitPrice, taxAmtUnitPrice } = computeLine(item);
  const hasDiscount = (item.discountPct || 0) > 0;
  const hasTax = (item.taxPct || 0) > 0;
  // Always show unit price without tax in the Price column
  const displayUnitPrice = discountedUnitPrice;

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0">
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm text-slate-700 truncate">{item.name}</span>
        {item.batch_number && (
          <span className="shrink-0 text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
            Batch: {item.batch_number}
          </span>
        )}
        {item.stock !== undefined && (
          <span className="shrink-0 text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
            Stock: {item.stock}
          </span>
        )}
        {(item.source === "plan" || item.source === "treatment_plan") && (
          <span className="shrink-0 text-[9px] font-medium text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
            Plan
          </span>
        )}
        {item.source === "manual" && (
          <span className="shrink-0 text-[9px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
            Added
          </span>
        )}
        {item.sampleType && (
          <span className="shrink-0 text-[9px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
            {item.sampleType}
          </span>
        )}
      </div>

      {/* Qty */}
      <div className="w-14 shrink-0">
        {readOnly ? (
          <div className="text-[12px] text-right text-slate-600">{item.quantity}</div>
        ) : (
          <input
            type="number"
            min="1"
            max={item.stock || 9999}
            value={item.quantity}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onChange(item.id, "quantity", "");
                return;
              }
              const parsed = Number(raw);
              if (!Number.isNaN(parsed)) {
                const maxQty = item.stock || 9999;
                onChange(item.id, "quantity", Math.max(1, Math.min(parsed, maxQty)));
              }
            }}
            onBlur={(e) => {
              if (e.target.value === "" || Number(e.target.value) < 1) {
                onChange(item.id, "quantity", 1);
              }
            }}
            className="w-full text-[12px] text-right border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-300"
          />
        )}
      </div>

      {/* Price (always without tax) */}
      <div className="w-24 shrink-0 text-right">
        {hasDiscount && !item.price_includes_tax ? (
          <div className="flex flex-col items-end leading-tight">
            <span className="text-[10px] text-slate-400 line-through">₹{fmtDec(item.price)}</span>
            <span className="text-[12px] font-semibold text-slate-700">₹{fmtDec(displayUnitPrice)}</span>
          </div>
        ) : (
          <span className="text-[12px] font-semibold text-slate-700">₹{fmtDec(displayUnitPrice)}</span>
        )}
      </div>

      {/* Tax (decimals) */}
      <div className="w-24 shrink-0 text-right">
        {hasTax ? (
          <span className="text-[12px] text-slate-600">
            ₹{fmtDec(taxAmtUnitPrice)} ({item.taxPct || 0}%)
          </span>
        ) : (
          <span className="text-[12px] text-slate-300"> </span>
        )}
      </div>

      {/* Line total (price + tax) */}
      <div className="w-20 shrink-0 text-right text-sm font-semibold text-slate-700">
        ₹{fmtDec(lineTotal)}
      </div>

      {/* ─── DELETE BUTTON ─── */}
      <div className="w-6 shrink-0 flex justify-end">
        {!readOnly && !hideDeleteButton && item.source !== "order" && (
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="p-1 hover:bg-red-50 rounded text-slate-400 hover:text-red-600 transition-colors"
            title="Remove from billing"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Catalog-search "add" row with Portal ────────────────────────────────

const CatalogSearchAddRow = ({
  endpoint,
  getName,
  getPrice,
  getSubLabel,
  getCatalogId,
  excludeIds = [],
  onSelect,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => updateDropdownPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    httpService
      .get(endpoint)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCatalog(list.filter((c) => c.status !== "inactive"));
      })
      .catch((err) => {
        console.error(`Failed to load catalog from ${endpoint}:`, err);
        toast.error("Failed to load catalog");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  const term = searchTerm.trim().toLowerCase();
  const results = catalog.filter((c) => {
    if (excludeIds.includes(getCatalogId(c))) return false;
    if (!term) return true;
    return (getName(c) || "").toLowerCase().includes(term);
  });

  const handleSelect = (c, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onSelect(c);
    setSearchTerm("");
    setIsOpen(false);
  };

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      setDropdownPosition(computeFixedDropdownPosition(inputRef.current, { maxHeight: 256, gap: 4 }));
    }
  };

  const handleFocus = () => {
    updateDropdownPosition();
    if (searchTerm.length >= 2 || results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.length >= 2) {
      updateDropdownPosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleDropdownMouseDown = (e) => {
    e.stopPropagation();
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto"
      style={{
        ...fixedDropdownStyle(dropdownPosition),
        minWidth: "200px",
      }}
      onMouseDown={handleDropdownMouseDown}
    >
      {results.length > 0 ? (
        results.map((c) => (
          <button
            key={getCatalogId(c)}
            type="button"
            onClick={(e) => handleSelect(c, e)}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-emerald-50 transition-colors border-b border-slate-50 last:border-0"
          >
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-slate-800 truncate">{getName(c)}</div>
              {getSubLabel?.(c) && (
                <div className="text-[10px] text-slate-400">{getSubLabel(c)}</div>
              )}
            </div>
            <span className="text-[11px] font-semibold text-emerald-600 shrink-0 ml-2">
              ₹{Number(getPrice(c) || 0).toFixed(0)}
            </span>
          </button>
        ))
      ) : (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
          {loading ? "Loading..." : "No results found"}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          placeholder={loading ? "Loading…" : placeholder}
          disabled={loading}
          className="w-full pl-8 pr-3 py-2 text-[12px] border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent bg-white disabled:bg-slate-50"
        />
      </div>
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

// ─── Product Search Dropdown for Medications ──────────────────────────────

const ProductSearchDropdown = ({ onSelect, excludeIds = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const isSelectingRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => updateDropdownPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await httpService.get(
          `/vendorproduct/searchvendorproducts?search=${encodeURIComponent(searchTerm)}`
        );
        if (response.code === 200) {
          const products = response.data || [];
          const excluded = new Set(excludeIds.map((id) => String(id)));
          const filtered = products.filter((p) => !excluded.has(String(p._id)));
          setResults(filtered);
          if (filtered.length > 0 && !isSelectingRef.current) {
            updateDropdownPosition();
            setIsOpen(true);
          }
        }
      } catch (err) {
        console.error("Failed to search products:", err);
        toast.error("Failed to search products");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, excludeIds]);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      setDropdownPosition(computeFixedDropdownPosition(inputRef.current, { maxHeight: 384, gap: 4 }));
    }
  };

  const handleSelect = (product, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (isSelectingRef.current) return;
    isSelectingRef.current = true;
    
    setIsOpen(false);
    
    if (onSelect) {
      onSelect(product);
    }
    
    setSearchTerm("");
    setResults([]);
    
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 300);
    
    setTimeout(() => {
      inputRef.current?.focus?.();
    }, 100);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2) {
      updateDropdownPosition();
      if (!isSelectingRef.current) {
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
      setResults([]);
    }
  };

  const handleFocus = () => {
    if (searchTerm.length >= 2 && results.length > 0 && !isSelectingRef.current) {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  const handleDropdownMouseDown = (e) => {
    e.stopPropagation();
  };

  const dropdownContent = isOpen && searchTerm.length >= 2 && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto"
      style={{
        ...fixedDropdownStyle(dropdownPosition),
        minWidth: "400px",
      }}
      onMouseDown={handleDropdownMouseDown}
    >
      {loading ? (
        <div className="px-3 py-4 text-[11px] text-slate-400 text-center flex items-center justify-center gap-2">
          <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          Searching products...
        </div>
      ) : results.length > 0 ? (
        <div>
          <div className="sticky top-0 bg-slate-50 px-3 py-1.5 border-b border-slate-200">
            <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              <div className="col-span-5 text-left">Product Name</div>
              <div className="col-span-2 text-left">Batch</div>
              <div className="col-span-2 text-left">MRP</div>
              <div className="col-span-2 text-left">Unit Price</div>
              <div className="col-span-1 text-left">Stock</div>
            </div>
          </div>
          {results.map((product) => {
            const stock = product.quantity_available || 0;
            const unitPrice = product.effective_sale_price || product.sale_price || product.mrp || 0;
            const mrp = product.mrp || 0;
            const hasDiscount = product.applied_discount && product.applied_discount.savings_percent > 0;
            const discountPercent = hasDiscount ? product.applied_discount.savings_percent : 0;
            
            return (
              <button
                key={product._id}
                type="button"
                onClick={(e) => handleSelect(product, e)}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full hover:bg-emerald-50 transition-colors border-b border-slate-50 last:border-0 text-left"
              >
                <div className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center">
                  <div className="col-span-5">
                    <div className="text-[12px] font-medium text-slate-800 truncate">
                      {product.name}
                      {hasDiscount && (
                        <span className="ml-1 inline-block bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                          {discountPercent}% OFF
                        </span>
                      )}
                    </div>
                    {product.sku_number && (
                      <div className="text-[10px] text-slate-400">SKU: {product.sku_number}</div>
                    )}
                    {product.product_brand_id?.name && (
                      <div className="text-[10px] text-slate-400">Brand: {product.product_brand_id.name}</div>
                    )}
                  </div>
                  <div className="col-span-2 text-[11px] text-slate-600 truncate">
                    {product.batches && product.batches.length > 0 ? (
                      product.batches.map((b, i) => (
                        <span key={i}>{b.batch_number || 'N/A'}</span>
                      ))
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                  <div className="col-span-2 text-[11px] font-semibold text-slate-700">
                    ₹{mrp.toFixed(0)}
                  </div>
                  <div className="col-span-2 text-[11px] font-semibold text-emerald-600">
                    ₹{unitPrice.toFixed(0)}
                    {hasDiscount && (
                      <span className="text-[10px] text-slate-400 line-through ml-1">
                        ₹{mrp.toFixed(0)}
                      </span>
                    )}
                  </div>
                  <div className="col-span-1">
                    <span className={`text-[11px] font-medium ${stock <= 0 ? 'text-red-500' : stock <= 10 ? 'text-amber-600' : 'text-slate-600'}`}>
                      {stock}
                    </span>
                    {product.unit && (
                      <span className="text-[10px] text-slate-400 ml-0.5">/{product.unit}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
          <div className="sticky bottom-0 bg-slate-50 px-3 py-1.5 border-t border-slate-200 text-[10px] text-slate-400 text-center">
            {results.length} product{results.length !== 1 ? 's' : ''} found
          </div>
        </div>
      ) : (
        <div className="px-3 py-4 text-[11px] text-slate-400 text-center">
          No products found matching "{searchTerm}"
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          placeholder="Search products by name, SKU, or batch..."
          className="w-full pl-8 pr-3 py-2 text-[12px] border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent bg-white"
        />
      </div>
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

// ─── Removed from bill (billing-only exclusion; SOAP/prescription unchanged) ─

const RemovedFromBillSection = ({ items, category, onRestore }) => {
  if (!items?.length) return null;

  return (
    <div className="bg-slate-50 rounded-xl border border-dashed border-slate-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Removed from bill
        </span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 last:border-0"
        >
          <span className="flex-1 text-sm text-slate-500 truncate">{item.name}</span>
          <span className="text-[12px] text-slate-400">₹{fmt(item.total || item.price || 0)}</span>
          <button
            type="button"
            onClick={() => onRestore(category, item.id)}
            className="shrink-0 text-[11px] font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
          >
            Add back
          </button>
        </div>
      ))}
    </div>
  );
};

const CategorySection = ({ title, accent, items, onChange, onRemove, readOnly = false, addRow = null }) => {
  const { base, discountAmt, taxAmt, lineTotal } = sumCategory(items);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-visible">
      <div className={`px-4 py-2 ${accent.bg}`}>
        <span className={`text-xs font-bold uppercase tracking-wider ${accent.text}`}>{title}</span>
      </div>

      {items.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
          <div className="flex-1 min-w-0 text-[10px] font-bold text-slate-400 uppercase">Item</div>
          <div className="w-14 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Qty</div>
          <div className="w-24 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Price</div>
          <div className="w-24 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Tax</div>
          <div className="w-20 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Total</div>
          <div className="w-6 shrink-0" />
        </div>
      )}

      {items.length === 0 && (
        <div className="px-4 py-4 text-[11px] text-slate-400">No items in this section.</div>
      )}

      {items.map((item) => (
        <ItemRow key={item.id} item={item} onChange={onChange} onRemove={onRemove} readOnly={readOnly} />
      ))}

      {addRow}
    </div>
  );
};

// ─── Main Billing Tab ──────────────────────────────────────────────────────

const BillingTab = forwardRef(({
  refId,
  vendorPetId,
  orderId,
  onDirtyChange,
  onSummaryChange,
  consultationFee,
  refreshTrigger,
  onBillingChange, // ⭐ NEW
  readOnly = false,
  isActive = false, 
}, ref) => {
  const {
    billingItems, payments, summary, fetchBillingData, loading, loadError,
    updateItem, removeItem, restoreItem, addManualItem,
    manualItems, modifiedItems, billingVersion,
    excludedItemsDetails,
    getBillingState, resetDirty, clearAll,
    calculateTotals,
    flushSave,
  } = useBilling();

  const [localRefresh, setLocalRefresh] = useState(0);

  const [hasBeenActive, setHasBeenActive] = useState(isActive);
  useEffect(() => {
    if (isActive) setHasBeenActive(true);
  }, [isActive]);

  // ⭐ Handle billing change notification
  const handleBillingChange = useCallback(() => {
    if (onBillingChange) {
      onBillingChange();
    }
  }, [onBillingChange]);

  // Wrap addManualItem to trigger change
  const addManualItemWithNotify = useCallback((category, item) => {
    const newItem = addManualItem(category, item);
    handleBillingChange();
    return newItem;
  }, [addManualItem, handleBillingChange]);

  // Wrap removeItem to notify
  const handleRemoveItem = useCallback(async (category, itemId) => {
    await removeItem(category, itemId);
    handleBillingChange();
  }, [removeItem, handleBillingChange]);

  // Wrap updateItem to notify
  const handleUpdateItem = useCallback((category, itemId, field, value) => {
    updateItem(category, itemId, field, value);
    handleBillingChange();
  }, [updateItem, handleBillingChange]);

  useEffect(() => {
    if (refreshTrigger > 0 || localRefresh > 0) {
      // Never force while local edits are pending — that was wiping newly
      // added products before autosave finished.
      fetchBillingData(false);
    }
  }, [refreshTrigger, localRefresh, fetchBillingData]);

  const handleRestoreItem = useCallback(async (category, itemId) => {
    await restoreItem(category, itemId);
    handleBillingChange();
  }, [restoreItem, handleBillingChange]);

  // ─── Add procedure from catalog ──────────────────────────────────────────
  const addProcedureFromCatalog = async (proc) => {
    const procId = proc._id;
    const wasExcluded = excludedItemsDetails.procedures?.some(
      (p) => p.id === procId || p.procedure_id === procId
    );
    if (wasExcluded) {
      await handleRestoreItem("Procedures", procId);
      toast.success(`${proc.name} added back to billing`);
      return;
    }

    addManualItemWithNotify("Procedures", {
      name: proc.name,
      price: Number(proc.fixed_price || proc.price || 0),
      quantity: 1,
      discountPct: 0,
      taxPct: 0,
      catalogId: proc._id,
    });
    toast.success(`Added ${proc.name} to procedures`);
  };

  // ─── Add lab test to billing ──────────────────────────────────────────
  const addLabTestToBilling = async (test) => {
    const excludedMatch = excludedItemsDetails.labTests?.find(
      (t) => t.lab_test_id === test._id || t.id === test._id
    );
    if (excludedMatch) {
      await handleRestoreItem("Lab Tests", excludedMatch.id);
      toast.success(`${test.name} added back to billing`);
      return;
    }

    addManualItemWithNotify("Lab Tests", {
      name: test.name,
      price: Number(test.price || 0),
      quantity: 1,
      discountPct: 0,
      taxPct: 0,
      catalogId: test._id,
      sampleType: test.sample_type || "",
    });
    toast.success(`Added ${test.name} to billing`);
  };

  // ─── Add product to medications ──────────────────────────────────────────
  const addProductToMedications = useCallback(async (product) => {
    const productId = product._id;
    const wasExcluded = excludedItemsDetails.medications?.some(
      (m) => m.id === productId || m.product_id === productId
    );
    if (wasExcluded) {
      await restoreItem("Medications", productId);
      handleBillingChange();
      toast.success(`${product.name} added back to billing`);
      return;
    }

    const priceWithTax = product.effective_sale_price || product.sale_price || product.mrp || 0;
    const taxPct = Number(product.gst_slab) || 0;
    const hasDiscount = product.applied_discount && product.applied_discount.savings_percent > 0;
    const discountPercent = hasDiscount ? product.applied_discount.savings_percent : 0;



    const itemToAdd = {
      name: product.name,
      price: Number(priceWithTax),
      quantity: 1,
      discountPct: discountPercent,
      taxPct,
      product_id: product._id,
      catalogId: product._id,
      sku_number: product.sku_number || product.sku || "",
      batch_number: product.batches?.[0]?.batch_number || product.batch_number || "",
      unit: product.unit || "",
      brand:
        product.product_brand_id?.name ||
        product.brand_name ||
        product.brand ||
        "",
      mrp: product.mrp || 0,
      sale_price: Number(priceWithTax),
      stock: product.quantity_available || 0,
    };

    addManualItemWithNotify("Medications", itemToAdd);
    toast.success(`Added ${product.name} to medications`);
  }, [addManualItemWithNotify, excludedItemsDetails.medications, restoreItem, handleBillingChange]);

  // ─── Compute totals from current state ──────────────────────────────────
  const totals = calculateTotals();
  const roundOff = totals.roundOff || 0;
  const totalCharges = totals.finalTotal || 0;
  const totalPaid = totals.totalPaid || 0;
  const balanceDue = totals.balanceDue || 0;

  const displayTotals = {
    base: totals.base || 0,
    discountAmt: totals.discountAmt || 0,
    taxAmt: totals.taxAmt || 0,
  };

  useEffect(() => {
    onSummaryChange?.({ totalCharges, totalPaid, balanceDue });
  }, [totalCharges, totalPaid, balanceDue, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      itemsByCategory: billingItems,
      payments,
      totalCharges,
      totalPaid,
      balanceDue,
    }),
    getBillingState: () => getBillingState(),
    clearAll: () => clearAll(),
    resetDirty: () => resetDirty(),
    getBalance: () => balanceDue,
    getTotal: () => totalCharges,
    refresh: fetchBillingData,
    flushSave: () => flushSave(),
  }));

  const procedureExcludeIds = billingItems.Procedures.map((p) => p.catalogId).filter(Boolean);
  const medicationExcludeIds = billingItems.Medications.map(
    (m) => m.product_id || m.catalogId || m.id,
  ).filter(Boolean);
  const labTestExcludeIds = billingItems["Lab Tests"].map((t) => t.catalogId).filter(Boolean);

  const treatmentPlanProducts = billingItems.Medications.filter(
    (i) => i.source === "treatment_plan" || i.source === "plan"
  );
  const treatmentPlanProductIds = new Set(
    treatmentPlanProducts
      .map((i) => String(i.product_id || i.catalogId || i.id || "").trim())
      .filter(Boolean),
  );
  const otherMedications = billingItems.Medications.filter((i) => {
    if (i.source === "treatment_plan" || i.source === "plan") return false;
    // Hide order/manual duplicates of treatment-plan extra products
    const pid = String(i.product_id || i.catalogId || "").trim();
    if (pid && treatmentPlanProductIds.has(pid)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-2 text-slate-400 text-[12px]">
          <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          Loading billing details…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-[12px] text-red-500">Couldn't load billing data. Please try reopening this tab.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-6 items-start">
      {/* ─── Left column: itemized sections ─────────────────────────── */}
      <div className="space-y-4">
        {/* Booked Service — read-only */}
        <CategorySection
          title="Service Booked"
          accent={{ bg: "bg-indigo-50", text: "text-indigo-700" }}
          items={billingItems.Consultation}
          readOnly
          addRow={null}
        />

        {/* Procedures */}
        {/* Procedures Performed + Treatment Plan Products */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-visible">
          <div className="px-4 py-2 bg-purple-50">
            <span className="text-xs font-bold uppercase tracking-wider text-purple-700">
              Procedures Performed
            </span>
          </div>

          {(billingItems.Procedures.length > 0 || treatmentPlanProducts.length > 0) && (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
              <div className="flex-1 min-w-0 text-[10px] font-bold text-slate-400 uppercase">Item</div>
              <div className="w-14 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Qty</div>
              <div className="w-24 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Price</div>
              <div className="w-24 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Tax</div>
              <div className="w-20 shrink-0 text-right text-[10px] font-bold text-slate-400 uppercase">Total</div>
              <div className="w-6 shrink-0" />
            </div>
          )}

          {billingItems.Procedures.length === 0 && treatmentPlanProducts.length === 0 && (
            <div className="px-4 py-4 text-[11px] text-slate-400">No items in this section.</div>
          )}

          {billingItems.Procedures.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              onChange={(id, field, value) => handleUpdateItem("Procedures", id, field, value)}
              onRemove={(id) => handleRemoveItem("Procedures", id)}
              hideDeleteButton
            />
          ))}

          {/* ⭐ Treatment plan's extra products, nested here instead of the Items table */}
          {treatmentPlanProducts.length > 0 && (
            <div className="px-4 py-1.5 bg-emerald-50/60 border-y border-emerald-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                Products (Treatment Plan)
              </span>
            </div>
          )}

          {treatmentPlanProducts.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              onChange={(id, field, value) => handleUpdateItem("Medications", id, field, value)}
              onRemove={(id) => handleRemoveItem("Medications", id)}
            />
          ))}

          {/* Procedures section */}
          {hasBeenActive && (
            <CatalogSearchAddRow
              endpoint="/vendorvetclinicservice/get-service-procedures?status=active"
              getName={(p) => p.name}
              getPrice={(p) => p.fixed_price}
              getCatalogId={(p) => p._id}
              excludeIds={procedureExcludeIds}
              onSelect={addProcedureFromCatalog}
              placeholder="Search procedures to add..."
            />
          )}

          <RemovedFromBillSection
            items={excludedItemsDetails.procedures}
            category="Procedures"
            onRestore={handleRestoreItem}
          />
        </div>

        {/* Medications */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-visible">
          <div className="px-4 py-2 bg-amber-50">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Items</span>
          </div>

          {otherMedications.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-2 px-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[32%]">Product</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[13%]">Batch #</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[9%]">Qty</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[15%]">Price/Unit</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[13%]">Total</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[14%]">Amount</th>
                    <th className="py-2 px-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-[4%]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {otherMedications.map((item) => {
                    const stock = item.stock || 0;
                    const unit = item.unit || '';
                    const priceWithTax = item.price || 0;
                    const taxPct = item.taxPct || 0;
                    const qty = item.quantity || 1;
                    
                    const priceWithoutTax = taxPct > 0 ? priceWithTax / (1 + taxPct / 100) : priceWithTax;
                    const taxPerUnit = priceWithTax - priceWithoutTax;
                    
                    const totalWithTax = priceWithTax * qty;
                    const totalWithoutTax = priceWithoutTax * qty;
                    const totalTax = taxPerUnit * qty;
                    
                    const mrp = item.mrp || priceWithTax;
                    const hasDiscount = item.hasDiscount || (mrp > priceWithTax);
                    const discountPercent = item.discountPct || (hasDiscount ? Math.round(((mrp - priceWithTax) / mrp) * 100) : 0);
                    const batchNumber = item.batch_number || '—';
                    
                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition">
                        <td className="py-2.5 px-3 text-left align-top">
                          <div className="font-medium text-[13px] text-slate-800 break-words whitespace-normal leading-snug">
                            {item.name}
                            {taxPct > 0 && (
                              <span className="text-[10px] text-slate-500 font-normal ml-1">
                                ({taxPct}% Tax)
                              </span>
                            )}
                            {item.source === "prescription" && (
                              <span className="text-[10px] text-blue-600 font-normal ml-1">
                                (Prescribed)
                              </span>
                            )}
                            {item.brand && (
                              <span className="text-[10px] text-slate-400 block font-normal">
                                Brand: {item.brand}
                              </span>
                            )}
                            {item.sku_number && (
                              <span className="text-[10px] text-slate-400 block font-normal">
                                SKU: {item.sku_number}
                              </span>
                            )}
                            {(item.morning > 0 || item.afternoon > 0 || item.night > 0) && (
                              <span className="text-[10px] text-slate-500 block font-normal">
                                Dosage: {item.morning > 0 && `${item.morning} Morning`} 
                                {item.afternoon > 0 && `, ${item.afternoon} Afternoon`}
                                {item.night > 0 && `, ${item.night} Night`}
                                {item.foodTiming && ` (${item.foodTiming})`}
                                {item.duration > 0 && ` · ${item.duration} day${item.duration > 1 ? 's' : ''}`}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                            {batchNumber}
                          </span>
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          <input
                            type="number"
                            min="1"
                            max={stock || 9999}
                            value={qty}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                handleUpdateItem("Medications", item.id, "quantity", "");
                                return;
                              }
                              const parsed = Number(raw);
                              if (!Number.isNaN(parsed)) {
                                const maxQty = stock || 9999;
                                handleUpdateItem("Medications", item.id, "quantity", Math.max(1, Math.min(parsed, maxQty)));
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === "" || Number(e.target.value) < 1) {
                                handleUpdateItem("Medications", item.id, "quantity", 1);
                              }
                            }}
                            className="w-[44px] text-center border border-slate-200 rounded px-1 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-emerald-300"
                          />
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          {hasDiscount ? (
                            <div className="flex flex-col items-center">
                              <span className="font-medium text-emerald-600 text-[13px]">
                                ₹{priceWithTax.toFixed(2)}
                                {unit && (
                                  <span className="text-slate-400 text-[10px] ml-0.5 font-normal">
                                    /{unit}
                                  </span>
                                )}
                              </span>
                              <span className="text-slate-400 line-through text-[11px]">
                                ₹{mrp.toFixed(2)}
                                {unit && (
                                  <span className="text-slate-400 text-[10px] ml-0.5">
                                    /{unit}
                                  </span>
                                )}
                              </span>
                              <span className="text-[10px] text-emerald-600 font-medium">
                                {discountPercent}% OFF
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <span className="font-medium text-[13px]">
                                ₹{priceWithTax.toFixed(2)}
                                {unit && (
                                  <span className="text-slate-400 text-[10px] ml-0.5 font-normal">
                                    /{unit}
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          <span className="font-semibold text-[13px] text-slate-800">
                            ₹{totalWithTax.toFixed(2)}
                          </span>
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-medium text-[13px] text-slate-800">
                              ₹{totalWithoutTax.toFixed(2)}
                            </span>
                            {taxPct > 0 && (
                              <span className="text-[10px] text-slate-400">
                                (+₹{totalTax.toFixed(2)} taxes)
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-2.5 px-2 text-center">
                          {item.source !== "order" ? (
                            <button
                              type="button"
                              onClick={() => handleRemoveItem("Medications", item.id)}
                              className="text-slate-300 hover:text-red-500 transition p-1"
                              title="Remove item"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <span className="text-[9px] text-slate-400" title="Booked service cannot be removed">
                              Booked
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-4 text-[11px] text-slate-400">
              {otherMedications.length === 0 && treatmentPlanProducts.length > 0 
                ? "No medications added. (Treatment plan products are shown above in Procedures section)" 
                : "No medications added."}
            </div>
          )}

          <div className="px-4 py-3 border-t border-slate-200">
            <ProductSearchDropdown
              onSelect={addProductToMedications}
              excludeIds={medicationExcludeIds}
            />
          </div>

          <RemovedFromBillSection
            items={excludedItemsDetails.medications}
            category="Medications"
            onRestore={handleRestoreItem}
          />
        </div>

        {/* Lab Tests - Billing ONLY (separate from Objective) */}
        <CategorySection
          title="Lab tests"
          accent={{ bg: "bg-blue-50", text: "text-blue-700" }}
          items={billingItems["Lab Tests"]}
          onChange={(id, field, value) => handleUpdateItem("Lab Tests", id, field, value)}
          onRemove={(id) => handleRemoveItem("Lab Tests", id)}
          addRow={
            <>
              {hasBeenActive && (
                <CatalogSearchAddRow
                  endpoint="/vendorvetclinicservice/get-lab-tests"
                  getName={(t) => t.name}
                  getSubLabel={(t) => t.sample_type}
                  getPrice={(t) => t.price}
                  getCatalogId={(t) => t._id}
                  excludeIds={labTestExcludeIds}
                  onSelect={addLabTestToBilling}
                  placeholder="Search lab tests to add..."
                />
              )}
              <RemovedFromBillSection
                items={excludedItemsDetails.labTests}
                category="Lab Tests"
                onRestore={handleRestoreItem}
              />
            </>
          }
        />
      </div> 

      {/* ─── Right column: Billing Summary & Payment History ─────────── */}
      <div className="lg:sticky lg:top-1 space-y-3">
        {/* Billing Summary Box */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800">Billing Summary</h2>
            <p className="text-[10px] text-slate-500">
              {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </p>
          </div>

          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal</span>
              <span className="font-semibold text-slate-700">₹{displayTotals.base.toFixed(2)}</span>
            </div>

            {displayTotals.discountAmt > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Discount</span>
                <span className="font-semibold text-red-500">-₹{displayTotals.discountAmt.toFixed(2)}</span>
              </div>
            )}

            {displayTotals.discountAmt > 0 && (
              <div className="flex justify-between text-slate-600 border-b border-slate-100 pb-1.5">
                <span>Net Amount</span>
                <span className="font-semibold text-slate-700">₹{(displayTotals.base - displayTotals.discountAmt).toFixed(2)}</span>
              </div>
            )}

            {displayTotals.taxAmt > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Tax</span>
                <span className="font-semibold text-slate-700">₹{displayTotals.taxAmt.toFixed(2)}</span>
              </div>
            )}

            {/* Round Off - Only show if there's a decimal value */}
            {roundOff !== 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Round Off</span>
                <span className={`font-semibold ${roundOff >= 0 ? 'text-slate-700' : 'text-red-500'}`}>
                  {roundOff >= 0 ? '+' : ''}₹{roundOff.toFixed(2)}
                </span>
              </div>
            )}

            <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
              <span className="font-bold text-slate-800">Total Amount</span>
              <span className="font-bold text-emerald-700">₹{totalCharges.toFixed(2)}</span>
            </div>

            <div className="flex justify-between text-slate-600">
              <span>Paid</span>
              <span className="font-semibold text-emerald-600">₹{totalPaid.toFixed(2)}</span>
            </div>

            <div className="flex justify-between pt-2 mt-1 border-t border-slate-200">
              <span className="font-bold text-slate-800">Balance due</span>
              <span className={`font-bold ${balanceDue > 0 ? "text-red-600" : "text-emerald-600"}`}>
                ₹{balanceDue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* ⭐ Payment History Box — below Billing Summary, same style */}
        {payments && payments.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Payment History</h3>
            <div className="space-y-2">
              {payments.map((payment) => (
                <div key={payment.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-slate-600">
                      {new Date(payment.date).toLocaleDateString()} • {payment.method}
                    </span>
                    {payment.note && <span className="text-xs text-slate-400">({payment.note})</span>}
                    {payment.receipt_url && (
                      <a
                        href={payment.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Receipt
                      </a>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-emerald-600">-₹{payment.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

BillingTab.displayName = "BillingTab";

export default BillingTab;